import Phaser from 'phaser';
import { MorseNoteSprite } from './MorseNote';
import { messageToNotes, type MorseNote } from '@/utils/morse';
import { MORSE_SPACING, SCORING_ZONES, type DifficultyConfig } from '@/utils/timing';
import { THEME } from '@/config/theme';

export interface NoteTrackConfig {
  message: string;
  difficulty: DifficultyConfig;
  hitZoneX: number;
  trackY: number;
  letterY: number;
  mask?: Phaser.Display.Masks.GeometryMask;
}

interface LetterGroup {
  letter: Phaser.GameObjects.Text;
  centerOffset: number; // Pre-calculated center position relative to scroll
  staticX: number;      // Where the letter waits before peeling
  state: 'static' | 'scrolling';
  peelStartX: number;
  transitionElapsed: number;
}

export class NoteTrack {
  private scene: Phaser.Scene;
  private notes: MorseNoteSprite[] = [];
  private noteQueue: MorseNote[] = [];
  private config: NoteTrackConfig;
  private nextNoteIndex: number = 0;
  private isPlaying: boolean = false;
  private nextAppendedCharIndex: number = 0;

  // Position-based tracking
  private scrollOffset: number = 0;
  private noteSpawnOffsets: number[] = [];

  // Incoming letters grouped with notes (parallel to SentTrack)
  private letterGroups: Map<number, LetterGroup> = new Map(); // charIndex → group
  private charCenters: Map<number, number> = new Map(); // charIndex → center offset
  private staticOrder: number[] = []; // ordered char indices for static stack

  constructor(scene: Phaser.Scene, config: NoteTrackConfig) {
    this.scene = scene;
    this.config = config;

    this.noteQueue = messageToNotes(config.message);
    this.nextAppendedCharIndex = this.noteQueue.length > 0
      ? Math.max(...this.noteQueue.map(n => n.charIndex)) + 1
      : 0;
    this.calculateSpawnOffsets();
    this.calculateCharCenters();
    this.seedInitialLetters();
  }

  private calculateSpawnOffsets(): void {
    const trackRightEdge = this.scene.cameras.main.width - 40;
    const distanceToTrackEdge = trackRightEdge - this.config.hitZoneX;

    let leftEdge = distanceToTrackEdge;

    for (let i = 0; i < this.noteQueue.length; i++) {
      const note = this.noteQueue[i];
      const prevNote = i > 0 ? this.noteQueue[i - 1] : null;

      if (prevNote) {
        let gap: number = MORSE_SPACING.symbolGap;

        if (note.charIndex !== prevNote.charIndex) {
          const letterPadding = MORSE_SPACING.dotWidth * 0.5;
          gap = MORSE_SPACING.letterGap + letterPadding;

          if (note.charIndex > prevNote.charIndex + 1) {
            gap = MORSE_SPACING.wordGap;
          }
        }

        const prevWidth = prevNote.symbol === '.' ? MORSE_SPACING.dotWidth : MORSE_SPACING.dashWidth;
        leftEdge += prevWidth + gap;
      }

      this.noteSpawnOffsets.push(leftEdge);
    }
  }

  private calculateCharCenters(): void {
    this.charCenters.clear();
    const accumulator = new Map<number, { char: string; centers: number[] }>();

    for (let i = 0; i < this.noteQueue.length; i++) {
      const note = this.noteQueue[i];
      const offset = this.noteSpawnOffsets[i];
      const noteWidth = note.symbol === '.' ? MORSE_SPACING.dotWidth : MORSE_SPACING.dashWidth;
      const noteCenter = offset + noteWidth / 2;

      if (!accumulator.has(note.charIndex)) {
        accumulator.set(note.charIndex, { char: note.expectedChar, centers: [] });
      }
      accumulator.get(note.charIndex)!.centers.push(noteCenter);
    }

    for (const [charIndex, data] of accumulator) {
      const avgCenter = data.centers.reduce((a, b) => a + b, 0) / data.centers.length;
      this.charCenters.set(charIndex, avgCenter);
    }
  }

  private seedInitialLetters(): void {
    this.staticOrder = Array.from(this.charCenters.keys()).sort((a, b) => a - b);
    for (const charIndex of this.staticOrder) {
      const centerOffset = this.charCenters.get(charIndex)!;
      const existing = this.letterGroups.get(charIndex);
      if (existing) {
        existing.centerOffset = centerOffset;
        continue;
      }
      const letterChar = this.noteQueue.find((n) => n.charIndex === charIndex)?.expectedChar ?? '';
      this.createLetterGroup(charIndex, letterChar, centerOffset, 0);
    }
    this.repositionStaticLetters();
  }

  start(_gameTime: number): void {
    this.isPlaying = true;
    this.scrollOffset = 0;
  }

  update(_time: number, delta: number): void {
    if (!this.isPlaying) return;

    const dx = this.config.difficulty.scrollSpeed * (delta / 1000);
    this.scrollOffset += dx;

    this.spawnUpcomingNotes();

    for (const note of this.notes) {
      note.update(_time, delta);
    }

    // Position and fade letters in sync with scroll (peel-off when the note reaches the static slot)
    for (const [charIndex, group] of this.letterGroups) {
      const desiredX = this.config.hitZoneX + (this.charCenters.get(charIndex)! - this.scrollOffset);

      if (group.state === 'static') {
        group.letter.x = group.staticX;
        group.letter.setAlpha(1);

        // Peel when the note reaches (or passes) the static slot to avoid any jump
        if (desiredX <= group.staticX) {
          group.state = 'scrolling';
          group.peelStartX = desiredX;
          group.transitionElapsed = 0;
          group.letter.x = desiredX;
        }
        continue;
      }

      // scrolling: follow the note position directly (no easing drift)
      group.letter.x = desiredX;

      const centerOffset = group.letter.x - this.config.hitZoneX;
      // Match note fading: opaque until past the hit zone center, then fade over one ok-width
      if (centerOffset >= 0) {
        group.letter.setAlpha(1);
      } else {
        const fadeRange = SCORING_ZONES.ok;
        const alpha = Math.max(0, Math.min(1, (centerOffset + fadeRange) / fadeRange));
        group.letter.setAlpha(alpha);
      }
    }

    this.cleanupOffscreenNotes();
  }

  private spawnUpcomingNotes(): void {
    const screenRight = this.scene.cameras.main.width;
    const spawnBuffer = 50;
    const spawnThreshold = this.scrollOffset + screenRight + spawnBuffer;

    while (this.nextNoteIndex < this.noteQueue.length) {
      const noteOffset = this.noteSpawnOffsets[this.nextNoteIndex];

      if (noteOffset > spawnThreshold) {
        break;
      }

      const noteData = this.noteQueue[this.nextNoteIndex];
      const spawnX = this.config.hitZoneX + (noteOffset - this.scrollOffset);

      this.ensureLetterGroup(noteData.charIndex, noteData.expectedChar);

      const noteSprite = new MorseNoteSprite(this.scene, {
        symbol: noteData.symbol,
        charIndex: noteData.charIndex,
        symbolIndex: noteData.symbolIndex,
        isLastInChar: noteData.isLastInChar,
        expectedChar: noteData.expectedChar,
        spawnX: spawnX,
        speed: this.config.difficulty.scrollSpeed,
        hitZoneX: this.config.hitZoneX,
        trackY: this.config.trackY,
      });

      if (this.config.mask) {
        noteSprite.setMask(this.config.mask);
      }

      this.notes.push(noteSprite);
      this.nextNoteIndex++;
    }
  }

  private cleanupOffscreenNotes(): void {
    this.notes = this.notes.filter(note => {
      if (!note.active) return false;
      if (note.isOffScreen()) {
        note.destroy();
        return false;
      }
      return true;
    });

    // Clean up offscreen letters
    for (const [charIndex, group] of this.letterGroups) {
      if (group.letter.x < -50) {
        group.letter.destroy();
        this.letterGroups.delete(charIndex);
      }
    }
  }

  /**
   * Get all active notes for hit detection
   */
  getNotes(): MorseNoteSprite[] {
    return this.notes;
  }

  isComplete(): boolean {
    return this.nextNoteIndex >= this.noteQueue.length && this.notes.length === 0;
  }

  setScrollSpeed(speed: number): void {
    if (!Number.isFinite(speed) || speed <= 0) return;
    this.config.difficulty.scrollSpeed = speed;
    for (const note of this.notes) {
      note.setSpeed(speed);
    }
  }

  getTotalNotes(): number {
    return this.noteQueue.length;
  }

  appendLetter(_char: string): void {
    // No-op: letters are now created automatically when notes spawn via letterGroups
  }

  appendLetterFromRightEdge(letter: string): number {
    const newNotes = messageToNotes(letter);
    if (newNotes.length === 0) {
      this.nextAppendedCharIndex++;
      return 0;
    }

    const charIndex = this.nextAppendedCharIndex++;
    const normalized = newNotes.map((n) => ({
      ...n,
      charIndex,
      expectedChar: letter.toUpperCase(),
    }));

    const trackRightEdge = this.scene.cameras.main.width - 40;
    const distanceToTrackEdge = trackRightEdge - this.config.hitZoneX;

    let leftEdge = this.scrollOffset + distanceToTrackEdge;
    if (this.noteQueue.length > 0) {
      const prevNote = this.noteQueue[this.noteQueue.length - 1];
      const prevOffset = this.noteSpawnOffsets[this.noteSpawnOffsets.length - 1];
      const prevWidth = prevNote.symbol === '.' ? MORSE_SPACING.dotWidth : MORSE_SPACING.dashWidth;
      const letterPadding = MORSE_SPACING.dotWidth * 0.5;
      const gap = MORSE_SPACING.letterGap + letterPadding;
      leftEdge = Math.max(leftEdge, prevOffset + prevWidth + gap);
    }

    for (let i = 0; i < normalized.length; i++) {
      const note = normalized[i];

      if (i > 0) {
        const prev = normalized[i - 1];
        const prevWidth = prev.symbol === '.' ? MORSE_SPACING.dotWidth : MORSE_SPACING.dashWidth;
        leftEdge += prevWidth + MORSE_SPACING.symbolGap;
      }

      this.noteQueue.push(note);
      this.noteSpawnOffsets.push(leftEdge);
    }

    // Calculate center offset for the new character
    const centers = normalized.map((n, idx) => {
      const offset = this.noteSpawnOffsets[this.noteSpawnOffsets.length - normalized.length + idx];
      const width = n.symbol === '.' ? MORSE_SPACING.dotWidth : MORSE_SPACING.dashWidth;
      return offset + width / 2;
    });
    const avgCenter = centers.reduce((a, b) => a + b, 0) / centers.length;
    this.charCenters.set(charIndex, avgCenter);
    this.staticOrder.push(charIndex);
    this.createLetterGroup(charIndex, letter.toUpperCase(), avgCenter, 0);
    this.repositionStaticLetters();

    return normalized.length;
  }

  private ensureLetterGroup(charIndex: number, char: string): void {
    if (this.letterGroups.has(charIndex)) return;

    const centerOffset = this.charCenters.get(charIndex) ?? 0;
    this.staticOrder.push(charIndex);
    this.createLetterGroup(charIndex, char, centerOffset, 0);
    this.repositionStaticLetters();
  }

  private createLetterGroup(charIndex: number, char: string, centerOffset: number, staticX: number): void {
    const letter = this.scene.add.text(
      staticX,
      this.config.letterY,
      char,
      {
        fontFamily: THEME.fonts.mono,
        fontSize: THEME.sizes.heading,
        color: THEME.colors.primary,
        stroke: THEME.stroke.color,
        strokeThickness: THEME.stroke.thickness,
      }
    ).setOrigin(0.5);

    this.letterGroups.set(charIndex, {
      letter,
      centerOffset,
      staticX,
      state: 'static',
      peelStartX: staticX,
      transitionElapsed: 0,
    });
  }

  private repositionStaticLetters(): void {
    const total = this.staticOrder.length;
    if (total === 0) return;
    const stackRight = this.scene.cameras.main.width - 40;
    const letterSpacing = 20;
    for (let i = 0; i < total; i++) {
      const charIndex = this.staticOrder[i];
      const group = this.letterGroups.get(charIndex);
      if (!group || group.state !== 'static') continue;
      const x = stackRight - (total - 1 - i) * letterSpacing;
      group.staticX = x;
      group.letter.x = x;
    }
  }

  getStaticLetterCount(): number {
    let count = 0;
    for (const group of this.letterGroups.values()) {
      if (group.state === 'static' && group.letter.alpha > 0) {
        count += 1;
      }
    }
    return count;
  }

  destroy(): void {
    for (const note of this.notes) {
      note.destroy();
    }
    this.notes = [];

    for (const group of this.letterGroups.values()) {
      group.letter.destroy();
    }
    this.letterGroups.clear();

    this.isPlaying = false;
  }
}
